import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import i18next from 'i18next';

import { S3Item } from 'types';
import { ProductOrderItem } from 'types/api/orders.type';
import { PriceType } from 'types/api/pricing.type';

import { BucketContext } from 'contexts';

import { usePriceVisible } from 'hooks/usePriceVisible';

import { formatOrderPrice, parsePrice } from 'helpers/parsePrice';
import { getResolvedPriceByType } from 'helpers/pricing';

import { StyleGroup } from 'pages/Orders/components/Order/components/ProductTable/utils/groupByStyle';

import { Image as ProductImage } from 'components/Image';

import { ColourRow } from '../ColourRow';
import * as S from './StyleRow.styles';

type StyleRowProps = {
  group: StyleGroup;
  isSend: boolean;
  control: Control;
  headerDeliveryCode?: string;
  selectedPriceType?: PriceType;
  onQuantityBlur: (
    item: ProductOrderItem,
    size: string,
    quantity: number,
    inputName?: string
  ) => Promise<void>;
  onDeliveryCodeChange: (styleKey: string, code: string) => Promise<void>;
  onDeleteColour: (item: ProductOrderItem) => void;
  onDeleteStyle: (styleKey: string) => void;
};

export function getProductName(
  productName: { en: string; it: string } | undefined,
  language: string
): string {
  if (!productName) return '';
  return language === 'it' ? productName.it : productName.en;
}

/**
 * Unit price for the selected price type — picks the first size that has a
 * resolved price with status FOUND for that type.
 * Falls back to the legacy `price` field when resolvedPrices is not populated.
 */
const getUnitPrice = (
  item: ProductOrderItem | undefined,
  selectedPriceType: PriceType | undefined
): { value: string | number; currency: string } => {
  if (!item?.sizes) return { value: '', currency: item?.currency || '' };

  // Find first size with a FOUND resolved price for the selected type
  for (const size of item.sizes) {
    if (size.resolvedPrices && size.resolvedPrices.length > 0) {
      const resolved = getResolvedPriceByType(size.resolvedPrices, selectedPriceType);
      if (resolved && resolved.status === 'FOUND' && resolved.price) {
        return {
          value: resolved.price.price,
          currency: resolved.price.currency,
        };
      }
    }
  }

  // No resolved price available — show dash
  return { value: '', currency: item.currency || '' };
};

/**
 * Computes total cost for a colour item using the selected price type.
 * Uses backend-provided resolvedTotalProductPrice for Wholesale (default);
 * falls back to per-size client calculation for other price types.
 */
const computeColourTotal = (
  item: ProductOrderItem,
  selectedPriceType: PriceType | undefined
): number => {
  // Backend pre-computes the resolved total using Wholesale prices
  const useBackendResolved =
    item.resolvedTotalProductPrice != null &&
    (!selectedPriceType || selectedPriceType === 'WHOLESALE');

  if (useBackendResolved) {
    return parsePrice(item.resolvedTotalProductPrice);
  }

  // Client-side calculation from per-size resolvedPrices
  const sizes = item.sizes ?? [];
  let total = 0;
  for (const size of sizes) {
    if (size.quantity <= 0) continue; // eslint-disable-line no-continue
    const resolved = getResolvedPriceByType(size.resolvedPrices, selectedPriceType);
    if (resolved && resolved.status === 'FOUND' && resolved.price) {
      total += size.quantity * parsePrice(resolved.price.price);
    }
  }
  return total;
};

function StyleRowComponent(props: StyleRowProps): React.ReactElement {
  const {
    group,
    isSend,
    control,
    headerDeliveryCode,
    selectedPriceType,
    onQuantityBlur,
    onDeleteColour,
    onDeleteStyle,
  } = props;

  const { t } = useTranslation();

  const priceVisible = usePriceVisible();

  const firstItem = group.colours[0];
  const productName = getProductName(firstItem?.productName, i18next.language);
  const imageKey = firstItem?.imageUrl ?? '';

  const { getUrl } = useContext(BucketContext);
  const [resolvedImageUrl, resolvedImageUrlSet] = useState<string>('');

  // The API returns a relative S3 key (e.g. "/IN26/thumbs/MU15000004_3KA_759.jpg").
  // Resolve it via Cognito-credentialed S3 to get a signed URL the browser can load.
  useEffect(() => {
    let cancelled = false;
    if (!imageKey) {
      resolvedImageUrlSet('');
      return () => {
        cancelled = true;
      };
    }
    getUrl(`catalogue/products${imageKey}`)
      .then((res: S3Item) => {
        if (!cancelled && res?.url) resolvedImageUrlSet(res.url);
      })
      .catch(() => {
        if (!cancelled) resolvedImageUrlSet('');
      });
    return () => {
      cancelled = true;
    };
  }, [imageKey, getUrl]);

  const unitPriceData = useMemo(
    () => getUnitPrice(firstItem, selectedPriceType),
    [firstItem, selectedPriceType]
  );
  const { currency } = unitPriceData;

  // Derive suggested retail price from resolvedPrices (RETAIL type)
  const retailPriceData = useMemo(() => {
    for (const colour of group.colours) {
      for (const size of colour.sizes ?? []) {
        const retail = size.resolvedPrices?.find((rp) => rp.type === 'RETAIL');
        if (retail?.status === 'FOUND' && retail.price) {
          return { value: retail.price.price, currency: retail.price.currency };
        }
      }
    }
    return undefined;
  }, [group.colours]);

  // Show suggested retail only when RETAIL price exists and user is NOT
  // already viewing RETAIL in the price type selector
  const showRetailHint = priceVisible && !!retailPriceData && selectedPriceType !== 'RETAIL';

  // Subtitle: "{productCode} - {mainFabricCode} - Size chart: {scale}"
  const styleSubtitle = [
    [group.productCode, group.mainFabricCode].filter(Boolean).join(' - '),
    firstItem?.sizeScaleCode
      ? `${t('orderHeader.sizeChart')}: ${firstItem.sizeScaleCode}`
      : '',
  ]
    .filter(Boolean)
    .join(' - ');

  const styleTotalQty = group.colours.reduce((acc, c) => acc + (c.totalQuantity ?? 0), 0);

  const styleTotalCost = useMemo(
    () => group.colours.reduce((acc, c) => acc + computeColourTotal(c, selectedPriceType), 0),
    [group.colours, selectedPriceType]
  );

  const initialDeliveryCode = headerDeliveryCode ?? '';

  // Price column label changes based on selected type
  const priceLabel = useMemo(() => {
    if (!selectedPriceType) return t('catalogue.wholesalePrice');
    const labelMap: Record<PriceType, string> = {
      DISTRIBUTOR: 'catalogue.distributorPrice',
      WHOLESALE: 'catalogue.wholesalePrice',
      RETAIL: 'catalogue.retailPrice',
    };
    return t(labelMap[selectedPriceType] as any); // eslint-disable-line
  }, [selectedPriceType, t]);

  return (
    <S.Section>
      <S.ImageCell>
        <ProductImage source={resolvedImageUrl} alt={productName || group.productCode} />
      </S.ImageCell>

      <S.Body>
        <S.HeaderRow data-testid="style-header">
          <S.InfoCell>
            <S.TitleLine>{productName || group.productCode}</S.TitleLine>
            <S.SubtitleLine data-testid="style-item-code">{styleSubtitle}</S.SubtitleLine>
          </S.InfoCell>

          <S.PriceCell data-testid="delivery-code-select">
            <S.PriceLabel>{t('orderHeader.deliveryCode')}</S.PriceLabel>
            <S.PriceValue>{initialDeliveryCode || '—'}</S.PriceValue>
          </S.PriceCell>

          {showRetailHint ? (
            <S.PriceCell>
              <S.PriceLabel>{t('orderHeader.sugg')}</S.PriceLabel>
              <S.PriceValue>
                {formatOrderPrice(retailPriceData!.currency, retailPriceData!.value)}
              </S.PriceValue>
            </S.PriceCell>
          ) : (
            <S.PriceCell />
          )}

          {priceVisible && (
            <S.PriceCell>
              <S.PriceLabel>{priceLabel}</S.PriceLabel>
              <S.PriceValue>
                {unitPriceData.value ? formatOrderPrice(currency, unitPriceData.value) : '—'}
              </S.PriceValue>
            </S.PriceCell>
          )}
        </S.HeaderRow>

        <S.ColoursContainer data-testid="colour-rows-container">
          {group.colours.map((item) => (
            <ColourRow
              key={item.uniqueId}
              item={item}
              isSend={isSend}
              control={control}
              selectedPriceType={selectedPriceType}
              onQuantityBlur={onQuantityBlur}
              onDeleteColour={onDeleteColour}
            />
          ))}
        </S.ColoursContainer>

        <S.StyleTotalsRow>
          {!isSend ? (
            <S.StyleTotalsRemoveCell
              type="button"
              data-testid="remove-style-btn"
              aria-label={t('orderTable.deletesItem')}
              onClick={() => onDeleteStyle(group.styleKey)}
            >
              {t('orderTable.removeStyle')}
            </S.StyleTotalsRemoveCell>
          ) : (
            <>
              {/* TODO fix this shift */}
              <S.StyleTotalsLabel />
              <S.StyleTotalsLabel />
            </>
          )}

          <S.StyleTotalsLabel>
            <S.StyleTotalsLabelText>{t('orderTable.totalStyleQty')}</S.StyleTotalsLabelText>
            <S.StyleTotalsValue>{styleTotalQty}</S.StyleTotalsValue>
          </S.StyleTotalsLabel>
          {priceVisible && (
            <S.StyleTotalsLabel>
              <S.StyleTotalsLabelText>{t('orderTable.totalStyleCost')}</S.StyleTotalsLabelText>
              <S.StyleTotalsValue>
                {formatOrderPrice(currency, styleTotalCost)}
              </S.StyleTotalsValue>
            </S.StyleTotalsLabel>
          )}
        </S.StyleTotalsRow>
      </S.Body>
    </S.Section>
  );
}

/** Memoized: on a large order only the style whose items changed re-renders. */
export const StyleRow = memo(StyleRowComponent);
